
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Mail, 
  Search, 
  Zap, 
  ShieldCheck, 
  CheckCircle2, 
  XCircle, 
  Database, 
  Activity, 
  RefreshCw,
  Send,
  Lock,
  Download
} from 'lucide-react';

export const EmailView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleVerify = () => {
    if (!email) return;
    setIsVerifying(true);
    setTimeout(() => {
      setResult({
        email,
        deliverable: true,
        smtpScore: 94,
        isDisposable: false,
        isFree: false,
        mxRecords: 'mx1.google.com',
        domainAge: '12.4 yrs',
        blacklisted: false,
        analysis: [
          { check: 'SMTP Handshake', status: 'Success' },
          { check: 'MX Propagation', status: 'Success' },
          { check: 'Catch-all Detection', status: 'Disabled' },
          { check: 'DLP Scan', status: 'Passed' }
        ]
      });
      setIsVerifying(false);
    }, 1200);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Email Validation</h1>
            <p className="text-sm text-slate-500 font-medium">Real-time SMTP verification & deliverability intelligence.</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[10px] font-black uppercase tracking-widest">MX-Node Synchronized</span>
      </div>

      <div className="bg-slate-900 p-8 rounded-[3rem] shadow-2xl relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-white text-lg font-black mb-6 flex items-center">
            <Search size={18} className="mr-2 text-blue-400" /> Mailbox Forensics
          </h2>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input 
                type="text" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="compliance@enterprise.com" 
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-12 py-4 text-white font-mono outline-none focus:ring-4 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <button 
              onClick={handleVerify}
              disabled={isVerifying}
              className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
            >
              {isVerifying ? 'Verifying SMTP...' : 'Run Audit'}
            </button>
          </div>
        </div>
      </div>

      {result && !isVerifying && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 animate-in zoom-in-95 duration-500">
          <div className="md:col-span-4 space-y-6">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm text-center">
              <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                <CheckCircle2 size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-900">Deliverable</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Trust Score: {result.smtpScore}%</p>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Domain Attributes</h4>
              <div className="space-y-4">
                <DataRow label="MX Server" value={result.mxRecords} />
                <DataRow label="Domain Age" value={result.domainAge} />
                <DataRow label="Disposable" value={result.isDisposable ? 'Yes' : 'No'} />
                <DataRow label="Free Provider" value={result.isFree ? 'Yes' : 'No'} />
                <DataRow label="Blacklisted" value={result.blacklisted ? 'Match Found' : 'Clean'} valueColor={result.blacklisted ? 'text-rose-600' : 'text-emerald-600'} />
              </div>
            </div>
          </div>

          <div className="md:col-span-8 bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
             <div className="flex items-center justify-between mb-8">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                  <Activity size={16} className="mr-2 text-blue-600" /> Validation Pipeline
                </h3>
             </div>
             
             <div className="divide-y divide-slate-100">
                {result.analysis.map((item: any, i: number) => (
                  <div key={i} className="py-5 flex items-center justify-between group">
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-all">
                        <ShieldCheck size={16} />
                      </div>
                      <span className="text-sm font-bold text-slate-700">{item.check}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                       <span className="text-[10px] font-black uppercase text-emerald-600">{item.status}</span>
                       <CheckCircle2 size={14} className="text-emerald-500" />
                    </div>
                  </div>
                ))}
             </div>

             <div className="mt-8 p-6 bg-slate-900 rounded-[2rem] text-white flex items-center justify-between shadow-xl shadow-slate-200">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Audit Reference</p>
                  <p className="text-sm font-mono text-blue-400">EV-84291</p>
                </div>
                <button className="bg-white/10 hover:bg-white/20 p-3 rounded-xl transition-all">
                   <Download size={20} />
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DataRow: React.FC<{ label: string, value: string, valueColor?: string }> = ({ label, value, valueColor = 'text-slate-900' }) => (
  <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
    <span className="text-slate-400">{label}</span>
    <span className={valueColor}>{value}</span>
  </div>
);
